// Inventory transactions (ARCH §3.4, PLR-1). PURE (ENG-1).
//
// Every slot mutation flows through these ops so solo clicks and (v1.1)
// server-arbitrated moves share one validated code path — UI code never writes a
// slot. Two layers:
//   • primitives — insert (distribute + return remainder), quickMove (shift), the
//     merge/split/swap helpers;
//   • the cursor state machine — `clickSlot`, the Minecraft grab/deposit/swap
//     semantics that the inventory screens replay.
import {
  type ItemStack, type Slots, emptyStack, isEmpty, canStack, cloneStack, slotAccepts,
} from './inventory';

/** Move up to `amount` of `src` into slot `i`, merging if same item. Returns how
 *  many were actually placed (0 if the slot rejects it or is full/foreign). */
function depositInto(slots: Slots, i: number, src: ItemStack, amount: number): number {
  if (isEmpty(src) || amount <= 0) return 0;
  const dst = slots.get(i);
  const cap = slots.maxStackOf(src.item);
  if (isEmpty(dst)) {
    if (!slotAccepts(slots, i, src)) return 0;
    const n = Math.min(amount, cap);
    slots.set(i, { item: src.item, count: n, components: src.components ? cloneStack(src).components : undefined });
    return n;
  }
  if (!canStack(dst, src)) return 0;
  const n = Math.min(amount, cap - dst.count);
  if (n <= 0) return 0;
  slots.set(i, { ...dst, count: dst.count + n });
  return n;
}

/**
 * Distribute `stack` across `range` (merge into matching stacks first, then fill
 * empty accepting slots). Mutates `slots`; returns the remainder (empty if all
 * placed). Does not mutate the input `stack`.
 */
export function insert(slots: Slots, stack: ItemStack, range: readonly number[]): ItemStack {
  if (isEmpty(stack)) return emptyStack();
  let remaining = stack.count;
  const src = stack;
  // Pass 1: top up existing same-item stacks.
  for (const i of range) {
    if (remaining <= 0) break;
    const dst = slots.get(i);
    if (isEmpty(dst) || !canStack(dst, src)) continue;
    remaining -= depositInto(slots, i, { ...src, count: remaining }, remaining);
  }
  // Pass 2: drop into empty slots.
  for (const i of range) {
    if (remaining <= 0) break;
    if (!isEmpty(slots.get(i))) continue;
    remaining -= depositInto(slots, i, { ...src, count: remaining }, remaining);
  }
  return remaining <= 0 ? emptyStack() : { ...cloneStack(src), count: remaining };
}

/** Shift-move slot `from` into `range` (another region). Leftover stays in `from`. */
export function quickMove(slots: Slots, from: number, range: readonly number[]): void {
  const src = slots.get(from);
  if (isEmpty(src)) return;
  const targets = range.filter((i) => i !== from);
  const remainder = insert(slots, src, targets);
  slots.set(from, remainder);
}

/** Whole slot `from` onto `to`: merge if same item, swap if not, place if empty. */
export function moveStack(slots: Slots, from: number, to: number): void {
  if (from === to) return;
  const src = slots.get(from);
  if (isEmpty(src)) return;
  const dst = slots.get(to);
  if (isEmpty(dst) || canStack(dst, src)) {
    const placed = depositInto(slots, to, src, src.count);
    slots.set(from, placed >= src.count ? emptyStack() : { ...src, count: src.count - placed });
  } else if (slotAccepts(slots, to, src) && slotAccepts(slots, from, dst)) {
    slots.set(to, src);
    slots.set(from, dst);
  }
}

// ── Cursor state machine ──────────────────────────────────────────────────────
export type ClickButton = 'left' | 'right' | 'middle';

export interface SlotClick {
  slot: number;
  button: ClickButton;
  shift?: boolean;
}

export interface ClickWindow {
  slots: Slots;
  /** Shift-click target region for slot `from` (the "other side" of the window). */
  quickMoveTargets(from: number): readonly number[];
  /** Slot `i` is an infinite creative source — grabbing never depletes it and
   *  depositing onto it deletes the cursor (creative item palette, D8). */
  isCreativeSource?(i: number): boolean;
  /** Slot `i` is take-only (a crafting/furnace output — cannot deposit into). */
  isOutput?(i: number): boolean;
  /** Fired when a take from an output slot succeeds (crafting consumes inputs). */
  onTake?(i: number, taken: ItemStack): void;
}

/**
 * Apply one slot click, returning the new cursor stack (Minecraft semantics).
 * `cursor` is what the mouse is holding; pass `emptyStack()` when nothing is held.
 */
export function clickSlot(win: ClickWindow, cursor: ItemStack, click: SlotClick): ItemStack {
  const { slots } = win;
  const i = click.slot;
  const creative = win.isCreativeSource?.(i) ?? false;
  const output = win.isOutput?.(i) ?? false;

  if (click.button === 'middle') {
    // Pick-block clone (creative): fill the cursor with a full stack, slot intact.
    const slot = slots.get(i);
    if (creative && isEmpty(cursor) && !isEmpty(slot)) {
      return { ...cloneStack(slot), count: slots.maxStackOf(slot.item) };
    }
    return cursor;
  }

  if (click.shift) {
    quickMoveClick(win, i, creative, output);
    return cursor; // shift-click never touches the cursor
  }

  if (creative) return creativeClick(slots, i, cursor, click.button);
  if (output) return outputClick(win, i, cursor);

  const slot = slots.get(i);
  if (click.button === 'left') {
    if (isEmpty(cursor)) {
      if (isEmpty(slot)) return cursor;
      slots.set(i, emptyStack());
      return slot; // grab whole stack
    }
    if (isEmpty(slot)) {
      if (!slotAccepts(slots, i, cursor)) return cursor;
      const placed = depositInto(slots, i, cursor, cursor.count);
      return placed >= cursor.count ? emptyStack() : { ...cursor, count: cursor.count - placed };
    }
    if (canStack(slot, cursor)) {
      const placed = depositInto(slots, i, cursor, cursor.count);
      return placed >= cursor.count ? emptyStack() : { ...cursor, count: cursor.count - placed };
    }
    // Different items → swap (only if the slot would accept the cursor).
    if (!slotAccepts(slots, i, cursor)) return cursor;
    slots.set(i, cursor);
    return slot;
  }

  // Right-click.
  if (isEmpty(cursor)) {
    if (isEmpty(slot)) return cursor;
    const take = Math.ceil(slot.count / 2);
    slots.set(i, { ...slot, count: slot.count - take });
    return { ...cloneStack(slot), count: take };
  }
  // Deposit a single item.
  if (isEmpty(slot)) {
    if (!slotAccepts(slots, i, cursor)) return cursor;
    slots.set(i, { ...cloneStack(cursor), count: 1 });
  } else if (canStack(slot, cursor) && slot.count < slots.maxStackOf(slot.item)) {
    slots.set(i, { ...slot, count: slot.count + 1 });
  } else {
    return cursor;
  }
  return cursor.count <= 1 ? emptyStack() : { ...cursor, count: cursor.count - 1 };
}

function quickMoveClick(win: ClickWindow, i: number, creative: boolean, output: boolean): void {
  const { slots } = win;
  const src = slots.get(i);
  if (isEmpty(src)) return;
  const targets = win.quickMoveTargets(i);
  if (output) {
    // Craft-all: repeatedly take the output into the inventory until it stops
    // producing or there's no room. `insert` adds the output's items to the
    // inventory; `onTake` then consumes one of each ingredient and RE-DERIVES the
    // output slot — so we must not overwrite it here (bounded loop, no infinite).
    for (let guard = 0; guard < 4096; guard++) {
      const out = slots.get(i);
      if (isEmpty(out)) break;
      const remainder = insert(slots, out, targets);
      if (remainder.count === out.count) break; // inventory full — nothing fit
      win.onTake?.(i, out); // consume ingredients + recompute the output slot
      if (!isEmpty(remainder)) break; // only partially fit — stop after this craft
    }
    return;
  }
  if (creative) {
    // Shift-click the palette → hand a full stack to the inventory.
    insert(slots, { ...cloneStack(src), count: slots.maxStackOf(src.item) }, targets);
    return;
  }
  quickMove(slots, i, targets);
}

function outputClick(win: ClickWindow, i: number, cursor: ItemStack): ItemStack {
  const { slots } = win;
  const out = slots.get(i);
  if (isEmpty(out)) return cursor;
  // Can only take when the cursor is empty or the same item with room.
  if (!isEmpty(cursor) && !(canStack(cursor, out) && cursor.count + out.count <= slots.maxStackOf(out.item))) {
    return cursor;
  }
  const next = isEmpty(cursor) ? cloneStack(out) : { ...cursor, count: cursor.count + out.count };
  slots.set(i, emptyStack());
  win.onTake?.(i, cloneStack(out));
  return next;
}

function creativeClick(slots: Slots, i: number, cursor: ItemStack, button: ClickButton): ItemStack {
  const slot = slots.get(i);
  if (isEmpty(cursor)) {
    if (isEmpty(slot)) return cursor;
    const cap = slots.maxStackOf(slot.item);
    const n = button === 'right' ? Math.max(1, Math.ceil(cap / 2)) : cap;
    return { ...cloneStack(slot), count: n }; // palette stays full
  }
  // Holding something and clicking the palette deletes (trash) the cursor.
  if (button === 'right') {
    return cursor.count <= 1 ? emptyStack() : { ...cursor, count: cursor.count - 1 };
  }
  return emptyStack();
}
